import { useEffect, useRef, useState } from "react";
import { ConversationMark } from "../OlliLogo";
import { OlliSendButton } from "../assistant/OlliSendButton";
import { RecentPrompts } from "../assistant/RecentPrompts";
import { VoiceButton, VoiceStrip, useVoiceInput } from "../VoiceInput";

/**
 * "Create with Olli" box (design: components/OlliComposer.tsx): type or speak a
 * prompt, then `onCreate(prompt, via)`. Voice is the real mic (useVoiceInput).
 * The page decides what happens on create; while it works it passes `sending`
 * (the button shows `buildingLabel`), and `error` when it fails
 * (see InstructionTemplates).
 */
export function OlliComposer({ title = "Create with Olli", description, placeholder, examples, actionLabel, buildingLabel, onCreate, autoListen = false, className = "", surface, sending = false, error = "" }) {
  const [prompt, setPrompt] = useState("");
  const [source, setSource] = useState("Text");
  const base = useRef("");
  const voice = useVoiceInput({ onTranscript: (spoken) => setPrompt(base.current ? `${base.current} ${spoken}` : spoken) });
  const listening = voice.status === "listening" || voice.status === "starting";

  const startVoice = () => {
    base.current = prompt.trim();
    setSource("Voice");
    voice.start();
  };
  useEffect(() => {
    if (autoListen) startVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = () => {
    const text = prompt.trim();
    if (!text || listening || sending) return;
    onCreate(text, source);
  };

  return (
    <section className={`rounded-lg bg-paper p-6 shadow-soft md:p-7 ${className}`} aria-label={title}>
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-ink">
          <ConversationMark tone="black" className="h-8 w-8" />
        </span>
        <div>
          <h2 className="text-h3 font-semibold">{title}</h2>
          <p className="mt-1 text-small text-muted">{description}</p>
        </div>
      </div>
      <label className="mt-5 block">
        <span className="sr-only">{placeholder}</span>
        <textarea
          value={prompt}
          onChange={(e) => { setPrompt(e.target.value); setSource("Text"); }}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") create(); }}
          rows={3}
          readOnly={listening}
          aria-busy={listening}
          placeholder={listening ? "Listening..." : placeholder}
          className={listening
            ? "w-full resize-y rounded-sm border border-sky-deep bg-paper px-4 py-3.5 text-body outline-none ring-4 ring-sky/30 transition-[box-shadow,border-color] duration-200 placeholder:text-muted"
            : "w-full resize-y rounded-sm border border-line bg-paper px-4 py-3.5 text-body outline-none transition-[box-shadow,border-color] duration-200 placeholder:text-muted focus:border-ink focus:ring-2 focus:ring-ink/10"}
        />
      </label>
      <div className="mt-3 flex flex-wrap gap-2">
        {examples.map((ex) => (
          <button key={ex} type="button" onClick={() => { setPrompt(ex); setSource("Text"); }} className="max-w-full rounded-full bg-mist px-3.5 py-2 text-left text-caption text-ink hover:bg-sky-soft">
            {ex}
          </button>
        ))}
      </div>
      {surface && <RecentPrompts surface={surface} onPick={(value) => { setPrompt(value); setSource("Text"); }} />}
      <VoiceStrip status={voice.status} levels={voice.levels} seconds={voice.seconds} notice={voice.notice} onStop={voice.stop} />
      {voice.error && <p role="alert" className="mt-3 text-caption text-danger">{voice.error}</p>}
      {error && <p role="alert" className="mt-3 text-caption text-danger">{error}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <VoiceButton status={voice.status} onStart={startVoice} onStop={voice.stop} />
        <span className="text-caption text-muted">
          {listening ? "Speak now. Olli types as you talk." : source === "Voice" ? "From voice. Edit the text if needed." : "Type, or tap the mic to speak"}
        </span>
        <div className="ml-auto">
          <OlliSendButton type="button" variant="ink" size="lg" label={actionLabel} sendingLabel={buildingLabel} sending={sending} onClick={create} disabled={!prompt.trim() || listening || sending} />
        </div>
      </div>
    </section>
  );
}
